import Mousetrap from 'mousetrap';
import type LetterPanel from '../uTheory.Notation/letterPanel';
import utils from '../utils';

export interface PlaybackButtonsOptions {
  playTonic: boolean;
  /** Default false */
  limitRepetitions: false | number;
  /** If not provided, defaults to limitRepetitions */
  limitTonic: false | number;
  /** The word to include on the play button, eg Play notes. Default: "note" */
  noteOrNotes: 'note' | 'notes' | 'Chord' | 'melody';
  /** The letter for the playback hot key, 'n' for 'notes' by default. */
  playbackHotkey: [string, ...string[]];
}

interface PlaybackButton {
  button: HTMLButtonElement;
  setRepetitions: (n: number) => void;
  increment: () => number | false;
  decrement: () => number | false;
  reset: () => void;
  readonly disabled: boolean;
}

type PlaybackButtonCallback = (value: string) => void;
/**
 * @param {string} text
 * @param {Integer|boolean} countDown
 * @param {Char|boolean} hotKey
 * @returns {object} ButtonObj {
 *    button: el,
 *    setRepetitions,
 *    increment,
 *    decrement
 * }
 */
const makeButton = (text: string, countDown: number | false = false): PlaybackButton => {
  if (countDown !== false && !utils.isInteger(countDown)) {
    throw new Error('countDown must be an integer or false.');
  }
  const initialCoundown = countDown;

  const button = document.createElement('button');
  button.classList.add('uButton', 'uLetterPanelButton');
  const nodeText = countDown ? `${text} ${countDown}` : text;
  button.innerHTML = nodeText;

  const err = () => {
    throw new Error('That button was not configured to limit repeated hearings.');
  };

  const setRepetitions =
    countDown !== false
      ? (n: number) => {
          countDown = n;
          button.innerHTML = `${text} (${n})`;
          button.disabled = n === 0;
        }
      : err;

  const decrement =
    countDown !== false
      ? () => {
          setRepetitions(--(countDown as number));
          return countDown;
        }
      : err;

  const increment =
    countDown !== false
      ? () => {
          setRepetitions(++(countDown as number));
          return countDown;
        }
      : err;

  const reset =
    countDown !== false
      ? () => {
          setRepetitions(initialCoundown as number);
        }
      : err;

  return {
    button,
    setRepetitions,
    increment,
    decrement,
    reset,
    get disabled() {
      return countDown === 0;
    },
  };
};

/**
 * @param {HTMLButton} button
 * @param {Function} callback
 * @param {string|string[]} hotKey
 * @returns button;
 */
const addCallback = (
  button: HTMLButtonElement,
  callback: () => void,
  hotKey: string | string[] | false = false
) => {
  if (!utils.isFunction(callback)) throw new Error('Callback must be a function.');
  button.addEventListener('click', callback);
  if (hotKey) {
    Mousetrap.bind(hotKey, callback);
  }
  return button;
};

let playTonicObj: PlaybackButton;
let playNoteObj: PlaybackButton;

/**
 * @param {domElement} el the dom element we add buttons to
 * @param {object} options
 *
 * options = {
 *   playTonic : false,
 *   limitRepetitions: false or number,
 *   limitTonic: defaults to limitRepetitions, false or number
 *   noteOrNotes: "note" (or "notes", or "chord", or "melody")
 *   playbackHotkey: ['n', 'N'] (for "notes" default);
 * }
 */
export default class PlaybackButtons {
  options: PlaybackButtonsOptions;

  el: HTMLDivElement;

  callbacks: PlaybackButtonCallback[];

  constructor(
    el: HTMLDivElement,
    options: Partial<PlaybackButtonsOptions> = {},
    letterPanel: LetterPanel
  ) {
    this.el = el;
    this.callbacks = [];
    const defaultOptions: Omit<PlaybackButtonsOptions, 'limitTonic'> = {
      playTonic: false,
      limitRepetitions: false,
      noteOrNotes: 'note',
      playbackHotkey: ['N', 'n'],
    };

    const mergedOptions = utils.merge(defaultOptions, options);
    this.options = {
      // By default, limitTonic mirrors limitRepetitions
      limitTonic: mergedOptions.limitTonic || mergedOptions.limitRepetitions,
      ...mergedOptions,
    };

    this.init(letterPanel);
  }

  init(letterPanel: LetterPanel) {
    const el = this.el;
    // Make the playTonic button & optional line break, if one is needed.
    if (this.options.playTonic) {
      playTonicObj = makeButton('Play T<u>o</u>nic', this.options.limitTonic);
      el.appendChild(playTonicObj.button);
      addCallback(
        playTonicObj.button,
        () => {
          this.playTonic();
        },
        ['o', 'O']
      );

      letterPanel.createCustomButton({
        tagName: 'br',
        perspective: ['square', 'portrait', 'tall'],
      });
    }

    const noteOrNotes = this.options.noteOrNotes
      .split('')
      .map((c, i) => {
        const letterMatch = c.toLowerCase() === this.options.playbackHotkey[0].toLowerCase();
        const firstOccurence =
          this.options.noteOrNotes.toLowerCase().indexOf(c.toLowerCase()) === i;
        if (letterMatch && firstOccurence) {
          return `<u>${c}</u>`;
        }
        return c;
      })
      .join('');
    playNoteObj = makeButton(`Play ${noteOrNotes}`, this.options.limitRepetitions);
    el.appendChild(playNoteObj.button);
    addCallback(
      playNoteObj.button,
      () => {
        this.play();
      },
      this.options.playbackHotkey
    );
    letterPanel.createCustomButton({
      tagName: 'br',
      class: 'uHideLastChild',
    });
  }

  play(tonic = false, limit = this.options.limitRepetitions) {
    const obj = tonic ? playTonicObj : playNoteObj;
    let count;
    if (obj.disabled) return 0;
    this.callbacks.forEach((cb) => {
      cb(tonic ? 'playTonic' : 'play');
    });
    if (limit) {
      count = obj.decrement();
    }
    return count;
  }

  playTonic() {
    return this.play(true, this.options.limitTonic);
  }

  reset() {
    if (this.options.limitRepetitions) {
      playNoteObj.reset();
    }
    if (this.options.playTonic && this.options.limitTonic) {
      playTonicObj.reset();
    }
  }

  /**
   * @param {Function} cb
   * @param {object} thisArg
   */
  addCallback(cb: PlaybackButtonCallback, thisArg = false) {
    cb = thisArg ? cb.bind(thisArg) : cb;
    this.callbacks.push(cb);
  }

  clearCallbacks() {
    this.callbacks.length = 0;
  }
}
